/**
 * Опции печатной версии страницы репо.
 *
 * Передаются от `PrintDialog` (на RepoPage) в URL `/r/:owner/:repo/print`,
 * где `PrintPage` фильтрует данные snapshot'а соответственно.
 *
 * Сериализация — короткие ключи: `f` (frame), `b` (backstage), `h` (health),
 * `m` (meta), `c` (cover), `d` (depth), `cv` (canvases). URL остаётся
 * читаемым, без длинных русскоязычных query-параметров.
 */

export type PrintDepth = 'summary' | 'full';

/** Допустимая глубина «Истории изменений». 0 = секция выключена. */
export type HistoryDepth = 0 | 10 | 20 | 50 | 100;
export const HISTORY_DEPTH_OPTIONS: readonly HistoryDepth[] = [0, 10, 20, 50, 100];

export interface PrintOptions {
    /** Секция «О проекте» (FRAME-нарративы). */
    frame:        boolean;
    /** Имена выбранных Канвасов. Пустой массив = все. */
    canvases:     string[];
    /** Секция «Поддержка» (backstage-нарративы). */
    backstage:    boolean;
    /** Только summary vs +structural blocks с цитатами. */
    depth:        PrintDepth;
    /** Подпись «генерировано X · дата» под summary. */
    showMeta:     boolean;
    /** Включать ли health-чекеры (по умолчанию нет — внутренняя кухня). */
    showHealth:   boolean;
    /** Первая страница-обложка (имя юнита, дата, число канвасов). */
    cover:        boolean;
    /** Сколько коммитов в секции «История изменений». 0 = выключена. */
    historyDepth: HistoryDepth;
}

export const DEFAULT_PRINT_OPTIONS: PrintOptions = {
    frame:        true,
    canvases:     [],
    backstage:    true,
    depth:        'full',
    showMeta:     false,
    showHealth:   false,
    cover:        true,
    historyDepth: 0,
};

export function toSearchParams(opts: PrintOptions): URLSearchParams {
    const p = new URLSearchParams();
    p.set('f', opts.frame      ? '1' : '0');
    p.set('b', opts.backstage  ? '1' : '0');
    p.set('h', opts.showHealth ? '1' : '0');
    p.set('m', opts.showMeta   ? '1' : '0');
    p.set('c', opts.cover      ? '1' : '0');
    p.set('d', opts.depth);
    if (opts.historyDepth > 0) p.set('hist', String(opts.historyDepth));
    if (opts.canvases.length > 0) p.set('cv', opts.canvases.join('|'));
    return p;
}

export function fromSearchParams(sp: URLSearchParams): PrintOptions {
    const bool = (key: string, fallback: boolean): boolean => {
        const v = sp.get(key);
        if (v === null) return fallback;
        return v === '1';
    };
    const depthRaw = sp.get('d');
    const depth: PrintDepth = depthRaw === 'summary' ? 'summary' : 'full';
    const cv = sp.get('cv');
    const canvases = cv ? cv.split('|').filter(Boolean) : [];
    const histRaw = parseInt(sp.get('hist') ?? '0', 10);
    const historyDepth: HistoryDepth = (HISTORY_DEPTH_OPTIONS as readonly number[]).includes(histRaw)
        ? (histRaw as HistoryDepth)
        : 0;
    return {
        frame:        bool('f', DEFAULT_PRINT_OPTIONS.frame),
        backstage:    bool('b', DEFAULT_PRINT_OPTIONS.backstage),
        showHealth:   bool('h', DEFAULT_PRINT_OPTIONS.showHealth),
        showMeta:     bool('m', DEFAULT_PRINT_OPTIONS.showMeta),
        cover:        bool('c', DEFAULT_PRINT_OPTIONS.cover),
        depth,
        canvases,
        historyDepth,
    };
}
